import React from 'react';
import { Box, Slider, Typography } from '@mui/material';
import { temperaturePolicy } from '../config/modelConfig';

interface TemperatureSliderProps {
  value?: number | null;
  onChange: (value: number) => void;
  /** Selected model — used to warn when temperature is locked/ignored. */
  model?: string | null;
  size?: 'small' | 'medium';
}

/**
 * Per-node sampling temperature. Unset displays as 0 (the Gemini/GPT-4 default).
 * GPT-5 family is locked at 1 by the provider; o-series ignores temperature.
 */
export const TemperatureSlider: React.FC<TemperatureSliderProps> = ({
  value,
  onChange,
  model,
  size = 'small',
}) => {
  const policy = temperaturePolicy(model);
  const display = typeof value === 'number' ? value : 0;
  const locked = policy.mode !== 'adjustable';

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2" gutterBottom sx={{ fontSize: size === 'small' ? '0.8rem' : undefined }}>
        Temperature: {locked ? policy.lockedValue ?? display : display}
      </Typography>
      <Slider
        value={locked ? (policy.lockedValue ?? 1) : display}
        onChange={(_, v) => onChange(v as number)}
        min={0}
        max={2}
        step={0.1}
        disabled={locked}
        marks={[
          { value: 0, label: '0' },
          { value: 1, label: '1' },
          { value: 2, label: '2' },
        ]}
        size={size}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
        {policy.hint}
      </Typography>
    </Box>
  );
};
